// Time anchors retain the message identity on the displayed conversation item.
// Array position alone never establishes correspondence with another capture.
export function assistantTurnIndexes(turns = []) {
  return turns.flatMap((turn, index) => turn?.role === "assistant" ? [index] : []);
}

export function buildTurnTimes(turns = []) {
  let previous = -Infinity;
  let anchored = 0;
  const times = turns.map((turn) => {
    const at = turn?.messageAt;
    if (!turn?.messageId || !Number.isFinite(at)) return null;
    // Reordered context cannot define a chronological work interval.
    if (at < previous) return null;
    previous = at;
    anchored += 1;
    return at;
  });
  return anchored ? { times, anchored, of: turns.length } : null;
}

/**
 * Bucket OS events onto the turn axis: an event lands on the first turn whose
 * dated completion is at or after it — the reply its work fed.
 *
 * Lead-in: the first dated turn also owns [floorTs, its time) — what the
 * session did before its first dated turn (a process can be dated at its own
 * start, well before the first prompt). Those are counted on that turn and
 * tallied as `lead`. Events before the lead-in (or before the first dated turn
 * when no floor is given) are counted as `before` rather than silently piled
 * onto it.
 *
 * Tail: the last dated turn also owns (its time, tailTs] — work after the
 * last dated observation (a reply lands after its prompt, and a session keeps
 * running after it). Without `tailTs`, events after the last dated turn remain
 * unassigned. turnSliceWindow takes the same `floorTs` and `tailTs`.
 *
 * @param events [{ ts, kind }] with `kind` an event type name
 * @param times  buildTurnTimes().times
 * @param kindOf event type -> band row key (systemKindOf)
 * @param opts.floorTs where the lead-in begins (systemSpan's fromTs)
 * @param opts.tailTs  where the last turn's tail ends (systemSpan's toTs)
 * @returns { perTurn: Map(turn -> { [rowKey]: n, total }), max, before, lead }
 */
export function bucketEventsByTurn(events = [], times = [], kindOf = () => null, { floorTs = null, tailTs = null } = {}) {
  const perTurn = new Map();
  let max = 0;
  let before = 0;
  let lead = 0;
  const anchors = (times || []).flatMap((at, index) => Number.isFinite(at) ? [{ at, index }] : []);
  const n = anchors.length;
  if (!n) return { perTurn, max, before: (events || []).length, lead };
  const leadFrom = Number.isFinite(floorTs) ? Math.min(floorTs, anchors[0].at) : anchors[0].at;
  const tailTo = Number.isFinite(tailTs) ? Math.max(tailTs, anchors[n - 1].at) : anchors[n - 1].at;
  for (const e of events || []) {
    const ts = e?.ts;
    const key = kindOf(e?.kind);
    if (!Number.isFinite(ts) || !key) continue;
    if (ts < leadFrom) {
      before += 1;
      continue;
    }
    if (ts > tailTo) continue;
    let lo = 0;
    if (ts < anchors[0].at) {
      lead += 1;
    } else if (ts > anchors[n - 1].at) {
      lo = n - 1;
    } else {
      // Binary search: first turn with times[t] >= ts.
      let hi = n - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (anchors[mid].at >= ts) hi = mid;
        else lo = mid + 1;
      }
    }
    const turn = anchors[lo].index;
    let b = perTurn.get(turn);
    if (!b) {
      b = { total: 0 };
      perTurn.set(turn, b);
    }
    b[key] = (b[key] || 0) + 1;
    b.total += 1;
    if (b[key] > max) max = b[key];
  }
  return { perTurn, max, before, lead };
}

/**
 * The inverse of bucketEventsByTurn for a run of turns: the inclusive
 * millisecond window [fromTs, toTs] holding exactly the events that rule
 * assigns to turns start..end, given the same `floorTs` and `tailTs`. A turn
 * owns (previous dated turn, its own time], so the lower bound is exclusive of
 * the previous anchor (+1 ms) and the upper bound includes the run's last
 * anchor. A run holding the first dated turn also holds the lead-in: its floor
 * is `floorTs` when that is earlier than the first anchor, else the first
 * anchor. A run reaching the last dated turn extends to `tailTs`.
 *
 * Returns { fromTs, toTs, tail }, or null when the run holds no dated turn.
 * `tail` says the upper bound is the tail ceiling rather than a turn anchor.
 */
export function turnSliceWindow(times = [], start = 0, end = start, { floorTs = null, tailTs = null } = {}) {
  const list = times || [];
  let prev = null;
  for (let i = Math.min(start, list.length) - 1; i >= 0; i -= 1) {
    if (Number.isFinite(list[i])) {
      prev = list[i];
      break;
    }
  }
  let first = null;
  for (let i = 0; i < list.length; i += 1) {
    if (Number.isFinite(list[i])) {
      first = list[i];
      break;
    }
  }
  let last = null;
  let lastIndex = -1;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (Number.isFinite(list[i])) {
      if (lastIndex < 0) lastIndex = i;
      if (i <= end && last == null) last = list[i];
    }
  }
  if (first == null || last == null || (prev != null && last <= prev)) return null;
  const fromTs = prev != null
    ? prev + 1
    : (Number.isFinite(floorTs) ? Math.min(floorTs, first) : first);
  const tail = end >= lastIndex && Number.isFinite(tailTs);
  return {
    fromTs,
    toTs: tail ? Math.max(tailTs, last) : last,
    tail,
  };
}
